import React, { useCallback, useMemo } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import {
  BaseAdminScreen,
  AdminEmptyScreenLayout,
} from '@/components';
import { PropertiesScreenSkeleton } from './PropertiesScreenSkeleton';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { useProperties } from '@/hooks/admin/useProperties';
import { useEntityScreen } from '@/hooks/admin/useEntityScreen';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { DesktopPropertiesView } from '@/components/admin/properties/desktop/DesktopPropertiesView';
import { PropertiesDataTable } from '@/components/admin/properties/PropertiesDataTable';
import { IPropertyRowData, IProperty } from '@/types/admin/properties';
import { downloadCsvFrom2DArray } from '@/utils/admin/csvExport';
import { DataTableProvider } from '@/contexts/admin/DataTableContext';

type PropertiesScreenProps = StackScreenProps<AdminStackParamList, 'Properties'>;

export const PropertiesScreen: React.FC<PropertiesScreenProps> = ({
  navigation,
}) => {
  const {
    data,
    tableRows,
    filteredTableRows,
    csvData,
    isLoading,
    error,
    selectedProperty,
    setSelectedProperty,
    showCreatePanel,
    setShowCreatePanel,
    formatCsvData,
  } = useProperties();

  const { layoutMode } = useEntityScreen<IPropertyRowData>({
    csvHeaders: ['marketingName', 'externalId', 'address'],
    formatCsvRow: (row) => [row.marketingName, row.externalId, row.address || ''],
  });

  // Determine if we have data
  const hasData = data.count > 0;
  
  // Show loading state if we're loading and have no data yet
  const showLoadingState = isLoading && !hasData;

  // Handle property selection
  const handlePropertySelect = useCallback((property: IPropertyRowData) => {
    // Find full property data from all rows (not filtered)
    const fullProperty = tableRows.find(p => p.id === property.id);
    if (!fullProperty) return;

    const fullPropertyData = data.rows.find(p => p.id === property.id);
    if (!fullPropertyData) return;

    if (layoutMode === 'desktop') {
      setSelectedProperty(fullPropertyData as IProperty);
    } else {
      navigation.navigate('EditProperty', { property: fullPropertyData as IProperty });
    }
  }, [data.rows, tableRows, setSelectedProperty, layoutMode, navigation]);

  // Handle create property
  const handleCreateProperty = useCallback(() => {
    if (layoutMode === 'desktop') {
      setShowCreatePanel(true);
    } else {
      navigation.navigate('CreateProperty');
    }
  }, [layoutMode, setShowCreatePanel, navigation]);

  // Handle close create panel
  const handleCloseCreatePanel = useCallback(() => {
    setShowCreatePanel(false);
  }, [setShowCreatePanel]);

  // Handle create success - refresh data
  const handleCreateSuccess = useCallback(() => {
    // Refresh the properties list
    // The API query will automatically refetch due to cache invalidation
  }, []);

  // Handle CSV export (mobile)
  const handleExportTable = useCallback(() => {
    if (Platform.OS === 'web') {
      const filename = `resio-properties-${new Date().toJSON().slice(0, 10)}`;
      try {
        downloadCsvFrom2DArray(csvData, filename);
      } catch (e) {
        console.error('Failed to download CSV', e);
      }
    } else {
      console.log('CSV export is available on web only.');
    }
  }, [csvData]);

  // Handle close property panel
  const handleClosePanel = useCallback(() => {
    setSelectedProperty(undefined);
  }, [setSelectedProperty]);

  // Desktop layout
  if (layoutMode === 'desktop') {
    return (
      <AdminErrorBoundary context="PropertiesScreenDesktop">
        <DataTableProvider tableKey="admin:properties" defaults={{}}>
          <DesktopPropertiesView
            data={{ rows: filteredTableRows, count: filteredTableRows.length }}
            isLoading={isLoading}
            csvData={csvData}
            selectedProperty={selectedProperty}
            showCreatePanel={showCreatePanel}
            onPropertySelect={handlePropertySelect}
            onCreateProperty={handleCreateProperty}
            onUpdateCsvData={formatCsvData}
            onClosePanel={handleClosePanel}
            onCloseCreatePanel={handleCloseCreatePanel}
            onCreateSuccess={handleCreateSuccess}
          />
        </DataTableProvider>
      </AdminErrorBoundary>
    );
  }

  // Mobile layout
  return (
    <AdminErrorBoundary context="PropertiesScreen">
      <BaseAdminScreen
        screenName="Properties"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <AdminEmptyScreenLayout
          title="Properties"
          emptyStateType="property"
          onAddPress={handleCreateProperty}
          showAddButton={true}
          buttonType="add"
          hasContent={true}
          disableScrollView={true}
        >
          {showLoadingState ? (
            <PropertiesScreenSkeleton />
          ) : (
            <DataTableProvider tableKey="admin:properties" defaults={{}}>
              <View style={styles.dataContainer}>
                <PropertiesDataTable
                  data={{ rows: filteredTableRows, count: filteredTableRows.length }}
                  isLoading={isLoading}
                  onPropertySelect={handlePropertySelect}
                  updateCsvData={formatCsvData}
                />
              </View>
            </DataTableProvider>
          )}
        </AdminEmptyScreenLayout>
      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
  dataContainer: {
    flex: 1,
    width: '100%',
  },
});
